import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

export type RelatedPackageRoom = Pick<OpportunityRelatedDeal, 'id' | 'room_asset' | 'sfid'>;

/** Whether a package's room follows its room asset's mapping or was chosen for that package directly. */
export type PackageRoomSource = 'matching' | 'override';

/** The room key meaning no room is chosen. */
export const UNMAPPED_ROOM_KEY = '';

/** A Salesforce deal is the package's source record, so its id is the package's persist id. */
export function getRelatedRoomPackageId(relatedRoom: Pick<OpportunityRelatedDeal, 'id' | 'sfid'>) {
  const packageId = relatedRoom.sfid || relatedRoom.id;

  return packageId ? String(packageId) : undefined;
}

export function getRoomAssetNameByPackageId(rooms: Array<RelatedPackageRoom>) {
  return Object.fromEntries(
    rooms.flatMap((room) => {
      const packageId = getRelatedRoomPackageId(room);
      return packageId && room.room_asset?.name ? [[packageId, room.room_asset.name]] : [];
    }),
  );
}

/**
 * The room each id resolves to: the CM's override, else the suggestion; dropped if that room no longer exists.
 * `roomKeyAliases` maps a draft room's key to the real id it gets once created.
 */
export function resolveRoomKeys({
  knownRoomIds,
  overrides,
  roomKeyAliases = {},
  suggestions,
}: {
  knownRoomIds: Array<string>;
  overrides: Record<string, string>;
  roomKeyAliases?: Record<string, string>;
  suggestions: Record<string, string>;
}) {
  const known = new Set(knownRoomIds);
  const resolved: Record<string, string> = {};

  Object.entries(suggestions).forEach(([id, roomKey]) => {
    const currentKey = roomKeyAliases[roomKey] ?? roomKey;

    if (known.has(currentKey)) {
      resolved[id] = currentKey;
    }
  });

  Object.entries(overrides).forEach(([id, roomKey]) => {
    const currentKey = roomKeyAliases[roomKey] ?? roomKey;

    if (roomKey === UNMAPPED_ROOM_KEY) {
      delete resolved[id];
    } else if (known.has(currentKey)) {
      resolved[id] = currentKey;
    }
  });

  return resolved;
}

export function mapAssetRoomKeysToPackages({
  assetIdByPackageId,
  assetRoomKeys,
}: {
  assetIdByPackageId: Record<string, string>;
  assetRoomKeys: Record<string, string>;
}) {
  const packageRoomKeys: Record<string, string> = {};

  Object.entries(assetIdByPackageId).forEach(([packageId, assetId]) => {
    if (assetRoomKeys[assetId]) {
      packageRoomKeys[packageId] = assetRoomKeys[assetId];
    }
  });

  return packageRoomKeys;
}
